import React from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import AICompetitionPreview from './AICompetitionPreview';

const AICompetitionSaveSection = ({
    newsletter,
    aiCompetitionsToSave,
    setAiCompetitionsToSave,
    onDuplicateFound,
    onCompetitionPreview,
    resolvedDuplicates,
    setResolvedDuplicates,
    onResetDecision,
    saveInProgress,
    setSaveInProgress,
    setSuccessAlertMessage,
    setSuccessAlertOpen,
    setErrorAlertMessage,
    setErrorAlertOpen,
    onSaved,
}) => {
    const [duplicateCheckResults, setDuplicateCheckResults] = React.useState({});
    const [checkingDuplicates, setCheckingDuplicates] = React.useState(false);

    // Extract AI-recommended competitions from newsletter
    const aiCompetitions = React.useMemo(() => {
        if (!newsletter?.content_data?.competitions) return [];

        return newsletter.content_data.competitions
            .map((comp, index) => ({ ...comp, original_index: index }))
            .filter(comp => comp.ai_recommended === true);
    }, [newsletter]);

    // Check all AI competitions for duplicates
    const checkAllDuplicates = async () => {
        setCheckingDuplicates(true);
        const results = {};
        let hasCheckErrors = false;

        for (const comp of aiCompetitions) {
            try {
                const response = await Axios.post(
                    `${process.env.REACT_APP_URL}/api/v1/competitions/check-duplicate`,
                    {
                        name: comp.competition_details?.name,
                        url: comp.competition_details?.url
                    },
                    {
                        headers: { Authorization: getAuthHeader() }
                    }
                );
                results[comp.original_index] = response.data;
            } catch (error) {
                console.error('Duplicate check error:', error);
                hasCheckErrors = true;
                results[comp.original_index] = {
                    exists: false,
                    error: error.message,
                    checkFailed: true
                };
            }
        }

        setDuplicateCheckResults(results);
        setCheckingDuplicates(false);

        // Warn user if any duplicate checks failed
        if (hasCheckErrors) {
            setErrorAlertMessage('Warning: Failed to check duplicates for some competitions. Please review carefully before saving.');
            setErrorAlertOpen(true);
        }
    };

    React.useEffect(() => {
        if (aiCompetitions.length > 0) {
            checkAllDuplicates();
        }
    }, [aiCompetitions.length]);

    // Handle saving selected competitions
    const handleSaveCompetitions = async () => {
        // Collect skipped/merged resolutions with their original_index so the server can
        // sync content_data.competitions entries (set competition_id, clear ai_recommended).
        const skippedResolutions = Object.entries(resolvedDuplicates)
            .filter(([, r]) => (r.action === 'skip' || r.action === 'merge') && r.existing_id)
            .map(([compIndex, r]) => ({
                competition_index: parseInt(compIndex, 10),
                existing_id: r.existing_id,
                action: r.action,
            }));

        if (aiCompetitionsToSave.length === 0 && skippedResolutions.length === 0) {
            setErrorAlertMessage('Please select at least one competition to save');
            setErrorAlertOpen(true);
            return;
        }

        setSaveInProgress(true);

        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${newsletter.uuid}/save-ai-competitions`,
                {
                    competitions: aiCompetitionsToSave,
                    skipped_resolutions: skippedResolutions,
                },
                {
                    headers: { Authorization: getAuthHeader() }
                }
            );

            const { results, competition_ids_added } = response.data;

            // Show results
            let successMessage = '';
            if (results.saved.length > 0) {
                successMessage = `Successfully saved ${results.saved.length} competition(s)! `;
            }
            if (skippedResolutions.length > 0) {
                successMessage += `${skippedResolutions.length} existing competition(s) linked. `;
            }
            if (results.errors.length > 0) {
                successMessage += `${results.errors.length} error(s) occurred.`;
            }
            if (competition_ids_added > 0) {
                successMessage += `Total ${competition_ids_added} competition(s) tracked.`;
            }

            setSuccessAlertMessage(successMessage);
            setSuccessAlertOpen(true);

            // Clear selections and any resolved-duplicate decisions: the affected
            // entries are now saved (ai_recommended: false) and will drop out of
            // the AI competitions list once the newsletter is refetched below.
            setAiCompetitionsToSave([]);
            if (setResolvedDuplicates) {
                setResolvedDuplicates({});
            }

            // Refetch the newsletter so content_data.competitions reflects the
            // server-side updates (competition_id linked, ai_recommended cleared).
            if (onSaved) {
                await onSaved();
            }

        } catch (error) {
            console.error('Save AI competitions error:', error);
            setErrorAlertMessage(
                error.response?.data?.error ||
                error.response?.data?.message ||
                (error.request ? 'Network error. Please check your connection.' : error.message) ||
                'Failed to save competitions'
            );
            setErrorAlertOpen(true);
        } finally {
            setSaveInProgress(false);
        }
    };

    if (aiCompetitions.length === 0) {
        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    AI-Recommended Competitions
                </Typography>
                <Alert severity="info">
                    No AI-recommended competitions found in this newsletter.
                </Alert>
            </Box>
        );
    }

    // The save flow accepts either new "create" selections or skip/merge resolutions, so
    // the button must be enabled when either source has at least one entry.
    const skipMergeCount = Object.values(resolvedDuplicates).filter(
        r => (r.action === 'skip' || r.action === 'merge') && r.existing_id
    ).length;
    const totalToProcess = aiCompetitionsToSave.length + skipMergeCount;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Save AI-Recommended Competitions to Database
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
                Review the AI-recommended competitions below and select which ones to save to your database.
                Competitions will be checked for duplicates automatically.
            </Alert>

            {checkingDuplicates && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Checking for duplicates...</Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                {aiCompetitions.map((comp) => {
                    const resolutionData = resolvedDuplicates[comp.original_index] || null;

                    return (
                        <AICompetitionPreview
                            key={comp.original_index}
                            competition={comp}
                            duplicateCheck={duplicateCheckResults[comp.original_index]}
                            selected={aiCompetitionsToSave.some(
                                item => item.competition_index === comp.original_index
                            )}
                            resolutionData={resolutionData}
                            onToggleSelect={(selected, mergeStrategy, selectedFields) => {
                                if (selected) {
                                    setAiCompetitionsToSave([
                                        ...aiCompetitionsToSave,
                                        {
                                            competition_index: comp.original_index,
                                            merge_strategy: mergeStrategy,
                                            selected_fields: selectedFields
                                        }
                                    ]);
                                } else {
                                    setAiCompetitionsToSave(
                                        aiCompetitionsToSave.filter(
                                            item => item.competition_index !== comp.original_index
                                        )
                                    );
                                }
                            }}
                            onDuplicateFound={(duplicateData) => {
                                onDuplicateFound({
                                    ...duplicateData,
                                    ai_competition: comp
                                });
                            }}
                            onResetDecision={() => onResetDecision(comp.original_index)}
                            onPreview={onCompetitionPreview}
                        />
                    );
                })}
            </Box>

            <Button
                variant="contained"
                color="primary"
                startIcon={saveInProgress ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSaveCompetitions}
                disabled={saveInProgress || totalToProcess === 0}
                size="large"
            >
                {saveInProgress
                    ? 'Saving...'
                    : skipMergeCount > 0 && aiCompetitionsToSave.length > 0
                        ? `Save ${aiCompetitionsToSave.length} new + ${skipMergeCount} linked`
                        : aiCompetitionsToSave.length > 0
                            ? `Save ${aiCompetitionsToSave.length} Selected Competition(s)`
                            : `Save ${skipMergeCount} Linked Competition(s)`
                }
            </Button>
        </Box>
    );
};

export default AICompetitionSaveSection;
